import React from "react";

const LineHeightDropdown: React.FC<{ editor: any }> = ({ editor }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [customHeight, setCustomHeight] = React.useState('');
  const [currentLineHeight, setCurrentLineHeight] = React.useState('1.0');
  
  // Map display values to TipTap values (display + 0.5 = TipTap)
  const lineHeights = [
    { name: '1.0', value: '1.5' },
    { name: '1.25', value: '1.75' },
    { name: '1.5', value: '2.0' },
    { name: '1.75', value: '2.25' },
    { name: '2.0', value: '2.5' }
  ];

  // Helper functions for value conversion with proper rounding
  const tiptapToDisplay = (tiptapValue: number) => {
    const result = tiptapValue - 0.5;
    return parseFloat(result.toFixed(2)).toString(); // Round to 2 decimal places
  };

  const displayToTiptap = (displayValue: number) => {
    const result = displayValue + 0.5;
    return parseFloat(result.toFixed(2)).toString(); // Round to 2 decimal places
  };

  const getCurrentLineHeight = React.useCallback(() => {
    if (editor.isActive('textStyle')) {
      const attributes = editor.getAttributes('textStyle');
      if (attributes.lineHeight) {
        const tiptapLineHeight = parseFloat(attributes.lineHeight);
        const displayValue = tiptapToDisplay(tiptapLineHeight);
        
        const matchingPreset = lineHeights.find(h => h.name === displayValue);
        if (matchingPreset) {
          return matchingPreset.name;
        }
        return displayValue;
      }
    }
    
    for (const height of lineHeights) {
      if (editor.isActive('textStyle', { lineHeight: height.value })) {
        return height.name;
      }
    }
    
    return '1.0'; // default
  }, [editor, lineHeights]);

  // Update current line height when editor changes
  React.useEffect(() => {
    const updateCurrentLineHeight = () => {
      setCurrentLineHeight(getCurrentLineHeight());
    };

    if (editor) {
      editor.on('selectionUpdate', updateCurrentLineHeight);
      editor.on('transaction', updateCurrentLineHeight);
      
      // Initial update
      updateCurrentLineHeight();
    }

    return () => {
      if (editor) {
        editor.off('selectionUpdate', updateCurrentLineHeight);
        editor.off('transaction', updateCurrentLineHeight);
      }
    };
  }, [editor, getCurrentLineHeight]);

  const applyLineHeight = (tiptapValue: string) => {
    editor.chain().focus().toggleTextStyle({ lineHeight: tiptapValue }).run();
  };

  const applyCustomHeight = () => {
    const displayValue = parseFloat(customHeight);
    if (customHeight && displayValue > 0 && displayValue <= 10) {
      const tiptapValue = displayToTiptap(displayValue);
      applyLineHeight(tiptapValue);
      setCustomHeight('');
      setIsOpen(false);
    }
  };

  const handleCustomHeightKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applyCustomHeight();
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="dropdown line-height-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center px-1 py-1"
        title={`Line Height: ${currentLineHeight}`}
        style={{ minWidth: '80px',maxWidth:"80px" }}
      >
        <i className="ti ti-line-height me-1 fs-15"></i>
        <span className="small fs-15">{currentLineHeight}</span>
        <i className="ti ti-chevron-down ms-1 fs-14"></i>
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show position-absolute mt-1 p-2" style={{ minWidth: '140px', zIndex: 1000 }}>
          {/* Custom Height Input */}
          <div className="mb-2">
            <label className="form-label fs-12 mb-1">Custom Line Height:</label>
            <div className="input-group input-group-sm">
              <input
                type="number"
                className="form-control form-control-sm"
                placeholder="1.0"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                onKeyPress={handleCustomHeightKeyPress}
                min="0.1"
                max="10"
                step="0.1"
              />
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={applyCustomHeight}
                disabled={!customHeight || parseFloat(customHeight) <= 0}
              >
                <i className="ti ti-check fs-12"></i>
              </button>
            </div>
          </div>
           
          {/* Preset Heights */}
          {lineHeights.map((height, index) => (
            <button
              key={index}
              onClick={() => {
                applyLineHeight(height.value);
                setIsOpen(false);
              }}
              className={`dropdown-item text-center ${
                currentLineHeight === height.name ? 'active' : ''
              }`}
            >
              {height.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LineHeightDropdown;
